import { z } from 'zod';

/**
 * Motion: how a template animates when it is rendered as a video or an animated banner.
 *
 * Everything is declarative and optional. A layer refers to presets by id (`enter`, `exit`,
 * `emphasis`) with timing and parameters, so a template keeps animating sensibly after smart
 * resize, longer text from the API or a different motion style. Times are milliseconds.
 *
 * Without `doc.motion` a template is static; with it, its static render is the "rest pose"
 * (every layer entered, nothing exited), so adding motion never changes existing image renders.
 */

export const MAX_MOTION_DURATION = 60_000;
export const MOTION_FPS = [24, 25, 30, 60] as const;
export const SPLIT_UNITS = ['none', 'line', 'word', 'char'] as const;
export const SPLIT_ORDERS = ['start', 'end', 'center', 'edges', 'random'] as const;

const ms = z.number().min(0).max(MAX_MOTION_DURATION);
const presetId = z.string().min(1).max(64);
const paramsSchema = z
  .record(z.string().max(32), z.union([z.number(), z.string().max(1000), z.boolean()]))
  .optional();

/** One preset placed on the timeline of a layer. */
export const motionStepSchema = z.object({
  preset: presetId,
  /**
   * Enter and emphasis: start after the layer's scene starts. Exit: finish this long before
   * the scene ends.
   */
  delay: ms.default(0),
  duration: z.number().min(16).max(MAX_MOTION_DURATION),
  /** Easing id from EASINGS, `cubic-bezier(...)`, or `spring(stiffness, damping)`. */
  easing: z.string().max(64).optional(),
  params: paramsSchema,
});
export type MotionStep = z.infer<typeof motionStepSchema>;

export const emphasisStepSchema = motionStepSchema.extend({
  /** How many times to play it; 0 = repeat until the layer exits or the scene ends. */
  repeat: z.number().int().min(0).max(1000).default(1),
  /** Pause between repetitions. */
  gap: ms.default(0),
});
export type EmphasisStep = z.infer<typeof emphasisStepSchema>;

/** Text animators: animate lines, words or letters one after another. */
export const splitSchema = z.object({
  by: z.enum(SPLIT_UNITS).default('none'),
  /** Delay between neighbouring pieces. */
  stagger: z.number().min(0).max(2000).default(40),
  order: z.enum(SPLIT_ORDERS).default('start'),
});
export type MotionSplit = z.infer<typeof splitSchema>;

/**
 * Manual key frame of a layer: where it is (offset from its place in the design), how big, how
 * turned and how opaque at time `t` (ms from the start of the video). Values a key leaves out
 * are the rest pose. `easing` shapes the move from this key to the next one.
 */
export const motionKeySchema = z.object({
  t: z.number().min(0).max(MAX_MOTION_DURATION),
  x: z.number().min(-20_000).max(20_000).optional(),
  y: z.number().min(-20_000).max(20_000).optional(),
  scale: z.number().min(0).max(50).optional(),
  rotate: z.number().min(-3600).max(3600).optional(),
  opacity: z.number().min(0).max(1).optional(),
  /** Blur in px at a 1080 px canvas (motion blur on fast moves, focus pulls). */
  blur: z.number().min(0).max(200).optional(),
  easing: z.string().max(64).optional(),
});
export type MotionKey = z.infer<typeof motionKeySchema>;

export const layerMotionSchema = z.object({
  enter: motionStepSchema.nullable().optional(),
  exit: motionStepSchema.nullable().optional(),
  emphasis: emphasisStepSchema.nullable().optional(),
  split: splitSchema.optional(),
  /** Scene id. Layers without a scene stay on screen for the whole video (logo, background). */
  scene: z.string().min(1).max(64).optional(),
  /** Manual key frames, sorted by time; played on top of the presets. */
  keys: z.array(motionKeySchema).max(100).optional(),
  /**
   * Text effect of a text layer (sliced title, kinetic words, 3D type…): drawn with the
   * layer's own text, font and colour, in place of the layer while it plays.
   */
  effect: z
    .object({
      kind: presetId,
      /** After the layer appears (the start of its scene), ms. */
      delay: ms.default(0),
      /** How long it plays; none = until the layer leaves. */
      duration: z.number().min(16).max(MAX_MOTION_DURATION).nullable().optional(),
      params: paramsSchema,
    })
    .nullable()
    .optional(),
});
export type LayerMotion = z.infer<typeof layerMotionSchema>;

export const transitionSchema = z.object({
  preset: presetId,
  duration: z.number().min(16).max(10_000).default(600),
  easing: z.string().max(64).optional(),
  params: paramsSchema,
});
export type MotionTransition = z.infer<typeof transitionSchema>;

/** Scenes ("frames" of an HTML5 banner): groups of layers shown one after another. */
export const motionSceneSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(64).default(''),
  duration: z.number().min(200).max(MAX_MOTION_DURATION),
  /** Transition from this scene into the next one (ignored on the last scene). */
  transition: transitionSchema.nullable().optional(),
});
export type MotionScene = z.infer<typeof motionSceneSchema>;

export const FX_LAYERS = ['front', 'back'] as const;

/**
 * Motion graphics of their own, drawn over (or under) the design: colour panels, shutters,
 * rings, confetti, light sweeps, a title card… They are generated from a kind and parameters
 * at render time, take colours from the template, and never change its layers.
 */
export const fxSchema = z.object({
  id: z.string().min(1).max(64),
  kind: presetId,
  start: ms.default(0),
  duration: z.number().min(16).max(MAX_MOTION_DURATION),
  /** Above the layers (transitions, flashes) or between the background and the layers. */
  z: z.enum(FX_LAYERS).default('front'),
  params: paramsSchema,
  /** Switched off: kept, not drawn. */
  hidden: z.boolean().optional(),
});
export type MotionFx = z.infer<typeof fxSchema>;

/** Camera moves of the whole composition: push-in, pan, shake… */
export const cameraStepSchema = z.object({
  id: z.string().min(1).max(64),
  preset: presetId,
  start: ms.default(0),
  duration: z.number().min(16).max(MAX_MOTION_DURATION),
  easing: z.string().max(64).optional(),
  params: paramsSchema,
});
export type CameraStep = z.infer<typeof cameraStepSchema>;

/** What a layer is in the design, as motion styles see it (see styles.ts). */
export const MOTION_ROLES = [
  'background',
  'photo',
  'container',
  'kicker',
  'headline',
  'subtitle',
  'body',
  'price',
  'badge',
  'cta',
  'logo',
  'decor',
] as const;
export type MotionRole = (typeof MOTION_ROLES)[number];

const role = z.enum(MOTION_ROLES);
const presetTiming = {
  /** ms from the start of the video. */
  start: ms.optional(),
  /** ms before the end of the video (outros). */
  beforeEnd: z.number().min(0).max(MAX_MOTION_DURATION).optional(),
  /** Once the element of this role has entered (plus `offset`); skipped without one. */
  after: role.optional(),
  offset: z.number().min(-10_000).max(10_000).optional(),
  duration: z.union([z.number().min(16).max(MAX_MOTION_DURATION), z.literal('rest')]),
};

/**
 * A motion preset made by a user or an AI agent: the same shape as the built-in styles,
 * openers, outros and accents (see styles.ts), with one name instead of translations. Only the
 * structure is checked here; `checkPresetSpec` (custom.ts) checks the ids it refers to.
 */
export const motionPresetSpecSchema = z.object({
  id: z.string().regex(/^my-[A-Za-z0-9_-]{2,48}$/, 'Custom preset ids start with "my-"'),
  category: z.enum(['style', 'opener', 'outro', 'accent']),
  name: z.string().trim().min(1).max(60),
  description: z.string().max(300).default(''),
  /** Layer style whose choreography the preset takes (styles and openers without `roles`). */
  base: presetId.optional(),
  /** Minimum video length. */
  duration: z.number().min(0).max(MAX_MOTION_DURATION).default(0),
  /** Delay between consecutive elements in reading order. */
  stagger: z.number().min(0).max(2000).default(150),
  /** Openers: how long the design stays hidden. */
  intro: z.number().min(0).max(10_000).optional(),
  /** Outros: how long the ending lasts. */
  span: z.number().min(0).max(20_000).optional(),
  requires: role.optional(),
  fallback: presetId.optional(),
  leave: z
    .object({
      preset: presetId,
      duration: z.number().min(16).max(5000),
      params: paramsSchema,
      keep: z.array(role).max(12).optional(),
    })
    .optional(),
  roles: z
    .partialRecord(
      role,
      z.object({
        enter: z
          .object({
            preset: presetId,
            duration: z.number().min(16).max(10_000).optional(),
            easing: z.string().max(64).optional(),
            params: paramsSchema,
            split: splitSchema.partial().optional(),
          })
          .optional(),
        emphasis: z
          .object({
            preset: presetId,
            duration: z.number().min(16).max(MAX_MOTION_DURATION).optional(),
            repeat: z.number().int().min(0).max(1000).optional(),
            gap: ms.optional(),
            fromStart: z.boolean().optional(),
            params: paramsSchema,
          })
          .optional(),
      }),
    )
    .optional(),
  fx: z
    .array(
      z.object({
        ...presetTiming,
        kind: presetId,
        z: z.enum(FX_LAYERS).optional(),
        params: paramsSchema,
        target: role.optional(),
        anchor: role.optional(),
        backdrop: z.boolean().optional(),
        unless: role.optional(),
        textFrom: z.array(role).max(4).optional(),
      }),
    )
    .max(16)
    .default([]),
  camera: z
    .array(z.object({ ...presetTiming, preset: presetId, params: paramsSchema }))
    .max(8)
    .default([]),
});
export type MotionPresetSpec = z.infer<typeof motionPresetSpecSchema>;
export type MotionPresetSpecInput = z.input<typeof motionPresetSpecSchema>;

/**
 * Music of the video: a track from the media library placed from `trimStart`, with fades. The
 * beat grid (tempo and first beat, found at upload) lets the choreography land on the beat.
 */
export const motionAudioSchema = z.object({
  src: z.string().min(1).max(2000),
  name: z.string().max(200).optional(),
  /** Length of the whole track, ms. */
  duration: z.number().min(0).max(3_600_000),
  trimStart: z.number().min(0).max(3_600_000).default(0),
  /** When it starts in the video, ms. */
  start: ms.default(0),
  /** How long it plays, ms (none: to the end of the video). */
  length: z.number().min(50).max(3_600_000).nullable().optional(),
  volume: z.number().min(0).max(1).default(1),
  fadeIn: z.number().min(0).max(10_000).default(0),
  fadeOut: z.number().min(0).max(10_000).default(1000),
  bpm: z.number().min(40).max(250).nullable().optional(),
  /** First beat in the track, ms. */
  beatOffset: z.number().min(0).max(3_600_000).default(0),
  /** Coarse waveform for the timeline, 0..1. */
  peaks: z.array(z.number().min(0).max(1)).max(1000).optional(),
  /** "Animate" puts moves on the beat. */
  sync: z.boolean().default(true),
});
export type MotionAudio = z.infer<typeof motionAudioSchema>;

export const TRACK_KINDS = ['voice', 'music', 'sfx'] as const;

/** Ids of tracks cut off the music (see audio-edit.ts); an API "@music" replaces them too. */
export const MUSIC_PART_PREFIX = 'music-part-';

/**
 * A sound placed on the timeline next to the music: voice-over, a second music bed, a sound
 * effect. `length` is how much of the file plays from `trimStart` (none: to its end).
 */
export const motionTrackSchema = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(TRACK_KINDS).default('voice'),
  src: z.string().min(1).max(2000),
  name: z.string().max(200).optional(),
  /** Length of the whole file, ms. */
  duration: z.number().min(0).max(3_600_000),
  /** When it starts in the video, ms. */
  start: ms.default(0),
  trimStart: z.number().min(0).max(3_600_000).default(0),
  length: z.number().min(50).max(3_600_000).nullable().optional(),
  volume: z.number().min(0).max(1).default(1),
  fadeIn: z.number().min(0).max(10_000).default(0),
  fadeOut: z.number().min(0).max(10_000).default(0),
  peaks: z.array(z.number().min(0).max(1)).max(1000).optional(),
});
export type MotionTrack = z.infer<typeof motionTrackSchema>;

export const docMotionSchema = z.object({
  /** Length of the video when there are no scenes. */
  duration: z.number().min(200).max(MAX_MOTION_DURATION).default(5000),
  fps: z
    .number()
    .int()
    .refine((v) => (MOTION_FPS as readonly number[]).includes(v), 'fps must be 24, 25, 30 or 60')
    .default(30),
  /** Motion style used by "Animate" (kept so the choreography can be rebuilt after edits). */
  style: z.string().max(64).optional(),
  /** Opener added on top of the layer style (title card, covers, camera); null = none. */
  opener: z.string().max(64).nullable().optional(),
  /** Ending of the video (end card, offer focus…); null = none. */
  outro: z.string().max(64).nullable().optional(),
  /** Accents in the middle of the video, tied to elements (a hit on the price…). */
  accents: z.array(z.string().max(64)).max(8).optional(),
  /** Custom presets in use, kept with the template so it animates the same anywhere. */
  custom: z.array(motionPresetSpecSchema).max(8).optional(),
  scenes: z.array(motionSceneSchema).max(20).default([]),
  fx: z.array(fxSchema).max(40).default([]),
  camera: z.array(cameraStepSchema).max(20).default([]),
  /** Animated banners: how many times to play (0 = forever). Videos play once. */
  loops: z.number().int().min(0).max(100).default(1),
  audio: motionAudioSchema.nullable().optional(),
  /** Voice-over, sound effects and other sounds on top of the music. */
  tracks: z.array(motionTrackSchema).max(30).default([]),
  /** Music level while a voice track plays (ducking); 1 = never lowered. */
  duck: z.number().min(0).max(1).default(0.3),
  /** Scene shown as the still picture (thumbnails, image renders); none = the first one. */
  poster: z.string().min(1).max(64).optional(),
});
export type DocMotion = z.infer<typeof docMotionSchema>;
